import { useContext, useMemo, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import styled from '@emotion/styled';
import { offset } from '@floating-ui/dom';
import { useFloating } from '@floating-ui/react';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { ActiveChatSessionContext, ChatSessionFiltersContext } from '@trinity/chat-components/components/Chat/context';
import { HeaderContextContext } from '@trinity/chat-components/components/Chat.Header/context';
import { useZIndexMiddleware } from '@trinity/chat-components/components/presentation/popper-modifiers';
import { ChatContextType } from '@trinity/chat';
import { calculateActiveFiltersCount } from './common';
import { FilterPaneProfile, FilterPaneWorkspace } from './FilterPane';
import { GroupFeaturesContext } from '@trinity/chat-components/containers/Chat';
import { ChatFilterButton } from '@trinity/chat-components/components/FilterButton/index.js';

type Props = {
  className?: string;
};

export function FilterButton({ className }: Props) {
  const [open, setOpen] = useState<boolean>(false);

  const zIndexMiddleware = useZIndexMiddleware();
  const { refs, floatingStyles } = useFloating({
    middleware: [offset(8), zIndexMiddleware],
    placement: 'bottom-start',
  });

  const handleClick = useCallback((e: React.MouseEvent) => {
    setOpen(prev => !prev);
    e.stopPropagation();
  }, []);

  const handleClickAway = useCallback(() => {
    setOpen(false);
  }, []);

  const ctx = useContext(HeaderContextContext);
  const activeFiltersCount = useActiveFiltersCount();

  const display = {
    activeFilters: activeFiltersCount > 0,
  };

  return (
    <Root className={className}>
      <ChatFilterButton
        ref={refs.setReference}
        variant="chart"
        onClick={handleClick}
        count={activeFiltersCount}
        aria-label="Filter" />
      {open && (
        <ClickAwayListener onClickAway={handleClickAway}>
          <div>
            {createPortal(
              <Popper
                ref={refs.setFloating}
                style={floatingStyles} >
                {ctx.displayContextType === ChatContextType.KolProfile
                  ? <FilterPaneProfile active={display.activeFilters} />
                  : <FilterPaneWorkspace active={display.activeFilters} />}
              </Popper>,
              document.body,
            )}
          </div>
        </ClickAwayListener>
      )}
    </Root>
  );
}

function useActiveFiltersCount() {
  const filters = useContext(ChatSessionFiltersContext);
  const { context } = useContext(ActiveChatSessionContext);
  const features = useContext(GroupFeaturesContext);

  return useMemo(() => calculateActiveFiltersCount({
    context,
    data: filters,
    features,
  }), [context, features, filters]);
}

const Root = styled.div({});

const Popper = styled.div({});